import { useMemo, useState, type ReactNode } from 'react';
import ImportedBadge from './ImportedBadge';

/**
 * Спільна таблиця даних у стилі сторінки «Співробітники» (компактна HTML-таблиця).
 *
 * Фічі (єдині для всіх сторінок сайту):
 *  - ресайз колонок (тягни межу заголовка);
 *  - сортування по кліку на заголовок (▲/▼, ↕ для несортованих);
 *  - значок «І» імпорту з іншої бази (колонка з importBadge: true показує бейдж
 *    перед значенням за полем external_source рядка);
 *  - double-click по рядку → onRowDoubleClick;
 *  - приглушення рядків (rowMuted): напр. не чинні / на видаленні.
 *
 * Колонки задаються конфігом ColumnDef. value(row) повертає вміст комірки
 * (ReactNode); sortValue(row) — значення для сортування (за замовч. value як рядок).
 */

export interface ColumnDef<T> {
  key: string;
  header: string;
  /** Вміст комірки. */
  cell: (row: T) => ReactNode;
  /** Значення для сортування (число/рядок). За замовч. — текстовий cell недоступний,
   *  тож для сортованих колонок задавай sortValue. */
  sortValue?: (row: T) => string | number;
  /** Початкова ширина, px (за замовч. auto/flex). */
  width?: number;
  align?: 'left' | 'center' | 'right';
  /** Показати бейдж імпорту перед вмістом (бере row.external_source). */
  importBadge?: boolean;
}

interface Props<T> {
  rows: T[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyText?: string;
  onRowDoubleClick?: (row: T) => void;
  /** Приглушити рядок (opacity): напр. не чинний/на видаленні. */
  rowMuted?: (row: T) => number | undefined;
  /** Поле external_source для importBadge (за замовч. 'external_source'). */
  importField?: string;
  /** Початкове сортування. */
  initialSortKey?: string;
  initialSortDir?: 'asc' | 'desc';
  /** Ключ для збереження ширин колонок у localStorage (per-table). Якщо не
   *  заданий — обчислюється автоматично з URL + складу колонок (стабільно
   *  розрізняє таблиці без правок усіх сторінок). */
  storageKey?: string;
}

// Префікс ключа localStorage для ширин колонок DataTable.
const WIDTHS_LS_PREFIX = 'dt-widths:';

export default function DataTable<T>({
  rows, columns, rowKey, loading, emptyText = 'Немає даних.',
  onRowDoubleClick, rowMuted, importField = 'external_source',
  initialSortKey, initialSortDir = 'asc', storageKey,
}: Props<T>) {
  const [sortKey, setSortKey] = useState<string | null>(initialSortKey ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);

  // Стабільний ключ персисту: явний storageKey АБО авто (шлях + ключі колонок).
  // Склад колонок розрізняє кілька таблиць на одній сторінці; шлях — між сторінками.
  const colSig = columns.map((c) => c.key).join(',');
  const lsKey = WIDTHS_LS_PREFIX + (
    storageKey ?? `${typeof window !== 'undefined' ? window.location.pathname : ''}|${colSig}`
  );

  // Ширини колонок (px) — змінюються ресайзом; ключ → width.
  // Iніціалізація: дефолтні width з колонок, поверх — збережені у localStorage
  // (тільки для колонок, що ще існують — нові беруть дефолт).
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    const w: Record<string, number> = {};
    for (const c of columns) if (c.width) w[c.key] = c.width;
    try {
      const raw = localStorage.getItem(lsKey);
      if (raw) {
        const saved = JSON.parse(raw) as Record<string, number>;
        const valid = new Set(columns.map((c) => c.key));
        for (const [k, v] of Object.entries(saved)) {
          if (valid.has(k) && typeof v === 'number' && v >= 50) w[k] = v;
        }
      }
    } catch { /* пошкоджений запис / приватний режим — ігноруємо, беремо дефолти */ }
    return w;
  });

  // Зберегти поточні ширини у localStorage. Викликається у кінці ресайзу
  // (onUp), а не на кожен рух миші — менше записів, без засмічення на mount.
  const persistWidths = (w: Record<string, number>) => {
    try {
      localStorage.setItem(lsKey, JSON.stringify(w));
    } catch { /* quota / приватний режим — не критично */ }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = col.sortValue!(a); const vb = col.sortValue!(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [rows, sortKey, sortDir, columns]);

  const toggleSort = (c: ColumnDef<T>) => {
    if (!c.sortValue) return;
    if (sortKey === c.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(c.key); setSortDir('asc'); }
  };

  const startResize = (key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = (e.currentTarget as HTMLElement).parentElement!;
    const startW = widths[key] ?? th.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) => {
      const w = Math.max(50, startW + ev.clientX - startX);
      setWidths((prev) => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Зберегти фінальні ширини після завершення drag (актуальний стан
      // дістаємо через функціональний setWidths, без stale-замикання).
      setWidths((cur) => { persistWidths(cur); return cur; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div className="data-grid-wrap">
    <table className="data-grid">
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              style={{
                width: widths[c.key], textAlign: c.align ?? 'left',
                cursor: c.sortValue ? 'pointer' : 'default',
              }}
              onClick={() => toggleSort(c)}
            >
              <span className="data-grid__th-label">
                {c.header}
                {c.sortValue && (
                  <span className="data-grid__sort">
                    {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                  </span>
                )}
              </span>
              <span className="data-grid__resizer"
                    onMouseDown={(e) => startResize(c.key, e)} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={columns.length} className="data-grid__msg">Завантаження…</td></tr>
        ) : sorted.length === 0 ? (
          <tr><td colSpan={columns.length} className="data-grid__msg">{emptyText}</td></tr>
        ) : sorted.map((row) => (
          <tr key={rowKey(row)}
              style={{ opacity: rowMuted?.(row), cursor: onRowDoubleClick ? 'pointer' : undefined }}
              onDoubleClick={() => onRowDoubleClick?.(row)}>
            {columns.map((c) => (
              <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                {c.importBadge ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <ImportedBadge source={(row as Record<string, unknown>)[importField] as string | null} />
                    <span>{c.cell(row)}</span>
                  </span>
                ) : c.cell(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}
